import React, { useEffect, useState, useRef } from 'react';
import { Box, Center, FlatList, Pressable, Spinner, Text } from 'native-base';
import Reply from './Reply';
import { useAuthState } from '../../../context/auth';
import { useEnvState } from '../../../context/env';
import { topicsAPI } from '../../../context/topics';
import { getLikedReplyIDs, uniqueReplyReducer } from '../../utils/helpers';
import { MIN_REPLIES_TO_SHOW, MAX_REPLIES_PER_BATCH } from '../../Constants';
import { useEditPostDispatch, useEditPostState } from '../../../context/edit';
import { useReplyState } from '../../../context/reply';
import _ from 'lodash';
const Replies = ({
  topic,
  loadContents,
  updateTopicReplies,
  likedReplyIDs,
  setLikedReplyIDs,
  addLikedReplyID,
  removeLikedReplyID,
  navigation,
  platformUser,
  showUserProfile,
  setPassedReactRef,
  forumKey,
  forSingleTopicScreen,
  trackForum,
  updateTopicPost
}) => {
  const replyListRef = useRef(null);
  const authState = useAuthState();
  const envState = useEnvState();
  const initialReplies = (topic === null || topic === void 0 ? void 0 : topic.recent.map(r => r.post)) || [];
  const [repliesList, setRepliesList] = useState(initialReplies);
  const [repliesStream, setRepliesStream] = useState([]);
  const [repliesToShow, setRepliesToShow] = useState(repliesList);
  // minus 1 to take off the first post.
  const [numOfRemainingReplies, setNumOfRemainingReplies] = useState(topic.posts_count - initialReplies.length - 1);
  const [collapsible, setCollapsible] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [loadMoreRepliesSpinner, setLoadMoreRepliesSpinner] = useState(false);
  const apiKey = platformUser !== null && platformUser !== void 0 && platformUser.discourseKey ? platformUser === null || platformUser === void 0 ? void 0 : platformUser.discourseKey : authState.api_key;
  const editPostDispatch = useEditPostDispatch();
  const {
    postUpdated,
    loadingNewReply
  } = useEditPostState();
  useReplyState;
  const [showSpinner, setShowSpinner] = useState(loadingNewReply);
  useEffect(() => {
    setShowSpinner(loadingNewReply);
  }, [loadingNewReply]);
  const showRemainingRepliesString = remainingReplies => {
    return remainingReplies && remainingReplies > 0 ? `Tap here to read ${remainingReplies} more ${remainingReplies === 1 ? 'reply' : 'replies'} ...` : '';
  };
  const updateInformationText = () => {
    if (repliesList.length == topic.posts_count - 1 && repliesToShow.length == repliesList.length && repliesToShow.length > MIN_REPLIES_TO_SHOW) {
      setInformationText('Collapse');
      setCollapsible(true);
    } else {
      setInformationText(showRemainingRepliesString(numOfRemainingReplies));
    }
  };
  useEffect(() => {
    updateInformationText();
  }, [numOfRemainingReplies, repliesToShow, topic.posts_count]);
  const [informationText, setInformationText] = useState(showRemainingRepliesString(numOfRemainingReplies));
  const getFirstReplyBatch = async () => {
    try {
      // minus 1 as first post isn't part of replies
      if (repliesToShow.length < topic.posts_count - 1) {
        const response = await topicsAPI.getSingleTopic(envState.base_url, apiKey, topic === null || topic === void 0 ? void 0 : topic.id);
        if (response && response.post_stream) {
          const {
            posts,
            stream
          } = response.post_stream;
          if (posts && stream) {
            let initSetOfReplies = posts.slice(1);

            /* we have to be careful here, if a topic has more than 20 replies
             * then the sortedReplies don't necessarily include what is in the initial repliesList */
            let sortedRepliesSoFar = uniqueReplyReducer([...initSetOfReplies, ...repliesList]);
            let newRemainingReplies = Math.max(0, topic.posts_count - sortedRepliesSoFar.length - 1);
            setRepliesList(sortedRepliesSoFar);
            const liked = getLikedReplyIDs(posts);
            setLikedReplyIDs(liked);
            setRepliesStream(stream);
            setNumOfRemainingReplies(newRemainingReplies);
            expandReplies(sortedRepliesSoFar); // explicitly call due to async state set
            return response;
          }
        }
        throw new Error('Failed to get single topic.');
      }
    } catch (err) {
      console.log('Error while loading the topic');
    }
  };
  const loadMoreReplies = async () => {
    // figure out the next batch of replies to get
    const loadedReplyIDs = repliesList.map(r => r.id);
    let remainingReplies = repliesStream.filter(s => !loadedReplyIDs.includes(s));
    if (remainingReplies.length > 0) {
      setLoadMoreRepliesSpinner(true);
      const nextBatch = remainingReplies.slice(0, MAX_REPLIES_PER_BATCH);
      try {
        const response = await topicsAPI.getSingleTopicMoreReplies(envState.base_url, apiKey, topic === null || topic === void 0 ? void 0 : topic.id, nextBatch);
        if (response && response.post_stream) {
          const {
            posts
          } = response.post_stream;
          if (posts) {
            /* even though uniqueReplyReducer also does a sort
             * we still have to do this because the API call above
             * returns us the first post, so we have to shave it back off */
            const sortedReplies = posts.sort((a, b) => a.post_number - b.post_number).slice(1);
            let sortedRepliesSoFar = uniqueReplyReducer([...repliesList, ...sortedReplies]);
            let newRemainingReplies = Math.max(0, topic.posts_count - sortedRepliesSoFar.length - 1);
            setRepliesList(sortedRepliesSoFar);
            expandReplies(sortedRepliesSoFar);
            setNumOfRemainingReplies(newRemainingReplies);
            setLoadMoreRepliesSpinner(false);
            return response;
          }
        }
        throw new Error('Failed to get more replies for single topic.');
      } catch (err) {
        console.log('Error while loading more replies');
      }
    } else {
      console.log('No more replies');
      setNumOfRemainingReplies(0);
      setLoadMoreRepliesSpinner(false);
    }
    return null;
  };
  const expandReplies = rList => {
    setRepliesToShow(rList || repliesList);
  };
  const collapseReplies = () => {
    setCollapsible(!collapsible);
    /* we should only see this if we have more than MIN_REPLIES_TO_SHOW replies
     * so it is safe to use */
    setRepliesToShow(repliesList.slice(-MIN_REPLIES_TO_SHOW));
    setNumOfRemainingReplies(topic.posts_count - MIN_REPLIES_TO_SHOW - 1);
  };
  const resetShownReplies = async () => {
    if (!repliesStream.length) {
      await getFirstReplyBatch();
    } else {
      await loadMoreReplies();
    }
    if (collapsible) {
      collapseReplies();
    } else {
      if (repliesList.length == topic.posts_count - 1 || repliesList.length === repliesStream.length - 1) {
        expandReplies(); // otherwise, the awaiting call above is still pending
      }
    }
  };
  const updatePost = async () => {
    const response = await topicsAPI.getSingleTopic(envState.base_url, apiKey, topic === null || topic === void 0 ? void 0 : topic.id, true);
    if (response && response.post_stream) {
      const {
        posts,
        stream
      } = response.post_stream;
      // new stream contains the old stream AND is longer
      if (_.difference(repliesStream, stream).length === 0 && stream.length > repliesStream.length) setRepliesStream(stream);
      if (posts) {
        const sortedReplies = posts.sort((a, b) => a.post_number - b.post_number).slice(1);
        let sortedRepliesSoFar = uniqueReplyReducer([...repliesList, ...sortedReplies]);
        setRepliesList(sortedRepliesSoFar);
      }
    }
  };
  useEffect(() => {
    if (postUpdated.topicId === topic.id) {
      updatePost();
      if (!(postUpdated !== null && postUpdated !== void 0 && postUpdated.updated)) {
        editPostDispatch({
          type: 'SET_POST_UPDATED',
          payload: {}
        });
      } else if (postUpdated !== null && postUpdated !== void 0 && postUpdated.updated) {
        // if we are here, it's a self-edit
        setIsChanged(true);
      }
    }
  }, [postUpdated]);
  const updateReplies = () => {
    // if any replies later than the current displayed replies are present, add them to the list
    const highestDisplayedPostNumber = _.max(repliesToShow.map(p => p.post_number)) || 0;
    let replies;
    if (isChanged || highestDisplayedPostNumber < _.max(repliesList.map(p => p.post_number))) {
      replies = repliesList.slice(-(repliesToShow.length + repliesList.length - repliesList.map(p => p.post_number).indexOf(highestDisplayedPostNumber)));
      expandReplies(replies);
      setNumOfRemainingReplies((repliesStream.length ? repliesStream.length : topic.posts_count) - replies.length - 1);
    }
    isChanged && setIsChanged(false);
  };
  useEffect(() => {
    updateReplies();
    setShowSpinner({
      loading: false,
      topicId: null
    });
  }, [repliesList]);
  const headerComponent = /*#__PURE__*/React.createElement(Box, {
    py: 2,
    alignItems: 'center',
    justifyContent: 'center'
  }, /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => resetShownReplies()
  }, /*#__PURE__*/React.createElement(Text, null, informationText)));
  const renderItem = ({
    item: reply
  }) => {
    return /*#__PURE__*/React.createElement(Reply, {
      updateTopicPost: updateTopicPost,
      topic: topic,
      reply: reply.post ? reply.post : reply,
      key: reply.id.toString(),
      updateTopicReplies: updateTopicReplies,
      loadContents: loadContents,
      likedReplyIDs: likedReplyIDs,
      addLikedReplyID: addLikedReplyID,
      removeLikedReplyID: removeLikedReplyID,
      navigation: navigation,
      showUserProfile: showUserProfile,
      forSingleTopicScreen: forSingleTopicScreen,
      forumKey: forumKey,
      setPassedReactRef: setPassedReactRef,
      trackForum: trackForum
    });
  };
  const scrollToBottom = () => {
    if (replyListRef.current) {
      replyListRef.current.scrollToEnd({
        animated: true
      });
    }
  };
  useEffect(() => {
    if (loadingNewReply) {
      scrollToBottom();
    }
  }, [loadingNewReply]);
  return repliesToShow.length > 0 || (showSpinner === null || showSpinner === void 0 ? void 0 : showSpinner.topicId) === topic.id ? /*#__PURE__*/React.createElement(Box, {
    w: "100%",
    h: forSingleTopicScreen ? null : '100%',
    px: 4,
    pt: 4,
    pb: 2,
    position: "relative",
    flexShrink: forSingleTopicScreen ? null : '1'
  }, /*#__PURE__*/React.createElement(FlatList, {
    data: repliesToShow,
    extraData: repliesToShow,
    renderItem: renderItem,
    keyExtractor: reply => reply.id.toString(),
    ListHeaderComponent: informationText ? headerComponent : null
  }), (showSpinner === null || showSpinner === void 0 ? void 0 : showSpinner.loading) && (showSpinner === null || showSpinner === void 0 ? void 0 : showSpinner.topicId) === topic.id && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    bg: "gray.500",
    opacity: 0.3,
    zIndex: 1,
    pointerEvents: "none"
  }), /*#__PURE__*/React.createElement(Center, {
    flex: 1,
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2
  }, /*#__PURE__*/React.createElement(Spinner, {
    mb: 3,
    size: 100
  }))), loadMoreRepliesSpinner && /*#__PURE__*/React.createElement(Center, {
    flex: 1
  }, /*#__PURE__*/React.createElement(Spinner, {
    mb: 3
  }), /*#__PURE__*/React.createElement(Text, null, "Loading More Posts..."))) : null;
};
export default Replies;
//# sourceMappingURL=Replies.js.map